import React from "react";
import { styled, useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import CssBaseline from "@mui/material/CssBaseline";
import MainAppBar from "../../components/AppBar";
import { Grid, IconButton } from "@mui/material";
import NavBarItems from "./components/NavBarItems";
import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import MenuIcon from '@mui/icons-material/Menu';

const drawerWidth = 300;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(8)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(9)} + 1px)`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

const CloseButtonContainer = styled("div")({
  marginLeft: "auto",
});

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  position: 'fixed', // Changed to fixed
  zIndex: theme.zIndex.drawer + 2, // Higher than main content
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": {
      ...openedMixin(theme),
      backgroundColor: theme.palette.light.main,
      boxShadow: theme.shadows[16], // Add shadow when open
      position: 'fixed', // Fixed positioning
    },
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": {
      ...closedMixin(theme),
      backgroundColor: theme.palette.light.main,
      position: 'fixed', // Fixed positioning
    },
  }),
}));

export default function MiniDrawer({ children, routes }) {
  const theme = useTheme();
  const [open, setOpen] = React.useState(false);

  const toggleDrawer = () => {
    setOpen((prev) => !prev);
  };

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <MainAppBar />

      {/* Drawer with fixed positioning */}
      <Drawer variant="permanent" open={open}>
        <Box
          sx={{
            backgroundColor: "secondary.main",
            cursor: "pointer",
            mt: 9,
            mx: 0.1,
            "&:hover .drawer-toggle-btn": {
              backgroundColor: "secondary.main",
            },
          }}
          onClick={toggleDrawer}
        >
          <DrawerHeader>
            <Box>
              {!open && (
                <IconButton
                  className="drawer-toggle-btn"
                  sx={{
                    marginRight: "8px",
                    ml: 1.2,
                    backgroundColor: "secondary.main",
                    color: "white",
                    transition: "background-color 0.3s ease",
                  }}
                  aria-label="Open drawer"
                >
                  <MenuIcon />
                </IconButton>
              )}
            </Box>
            <Box>
              {open && (
                <CloseButtonContainer>
                  <IconButton
                    className="drawer-toggle-btn"
                    sx={{
                      backgroundColor: "primary.main",
                      color: "white",
                      transition: "background-color 0.3s ease",
                      transform: open ? "rotate(180deg)" : "none",
                      transitionProperty: "background-color, transform",
                      transitionDuration: "0.3s",
                    }}
                    aria-label={open ? "Close drawer" : "Open drawer"}
                  >
                    <DoubleArrowIcon />
                  </IconButton>
                </CloseButtonContainer>
              )}
            </Box>
          </DrawerHeader>
        </Box>

        <NavBarItems
          routes={routes}
          open={open}
          openDrawer={() => setOpen(true)}
          closeDrawer={() => setOpen(false)}
        />
      </Drawer>
      {/* Main content - stays fixed in place */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          paddingLeft: 2,
          marginTop: 3,
          width: '100%',
          position: 'relative',
          marginLeft: 6.5
          // No margin adjustment needed since drawer is fixed/overlay
        }}
      >
        <Grid container>
          <DrawerHeader /> {/* This might need adjustment */}
          {children}
        </Grid>
      </Box>
    </Box>
  );
}